import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import type { DetailsModel } from '@core/components/ui-clinng-status-alert/ui-clinng-status-alert.component';
import type { TicketDetails } from '@core/components/ui-clinng-ticket-info/ui-clinng-ticket-info.component';
import { UiClinngButtonComponent } from '@core/components/ui-clinng-button/ui-clinng-button.component';
import { UiClinngDownloadButtonComponent } from '@core/components/ui-clinng-download-button/ui-clinng-download-button.component';
import { UiClinngErrorModalComponent } from '@core/components/ui-clinng-error-modal/ui-clinng-error-modal.component';
import { UiClinngFormFieldComponent } from '@core/components/ui-clinng-form-field/ui-clinng-form-field.component';
import { UiClinngStaticHeaderComponent } from '@core/components/ui-clinng-static-header/ui-clinng-static-header.component';
import { UiClinngStatusAlertComponent } from '@core/components/ui-clinng-status-alert/ui-clinng-status-alert.component';
import { UiClinngTicketInfoComponent } from '@core/components/ui-clinng-ticket-info/ui-clinng-ticket-info.component';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DownloadTransfiyaVoucherComponent } from './download-transfiya-voucher/download-transfiya-voucher.component';
import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { IconsEnum } from '@core/models/icons.enum';
import { CaptureScreenService } from '@core/services/capture-screen.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DaviplataService } from '@core/services/daviplata.service';
import type { PaymentAttribute, PaymentDetails } from '@core/services/daviplata.interface';
import { CustomSpinnerComponent } from '@core/components/ui-custom-spinner/custom-spinner.component';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TimerPipe } from '@core/pipes/timer.pipe';
import { CheckoutClinngModalLoadingComponent } from '@core/components/checkout-clinng-modal-loading/checkout-clinng-modal-loading.component';
import { ResponsiveService } from '@core/services/responsive.service';
import { finalize, timeout } from 'rxjs';

enum Status {
  PENDING = '2',
  EXPIRED = '3',
  APPROVED = '5',
  DENIED = '6',
}

@Component({
  selector: 'checkout-form-transfiya',
  standalone: true,
  imports: [
    UiClinngButtonComponent,
    UiClinngTicketInfoComponent,
    UiClinngStaticHeaderComponent,
    UiClinngFormFieldComponent,
    MatIconModule,
    MatCheckboxModule,
    UiClinngDownloadButtonComponent,
    DownloadTransfiyaVoucherComponent,
    NgClass,
    CustomSpinnerComponent,
    ReactiveFormsModule,
    TimerPipe,
    AsyncPipe,
    UiClinngStatusAlertComponent
],
  templateUrl: './checkout-form-transfiya.component.html',
  styleUrl: './checkout-form-transfiya.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe, DecimalPipe]
})
export class CheckoutFormTransfiyaComponent implements OnInit {
  private readonly screenRecordService = inject(CaptureScreenService);
  readonly responsiveService = inject(ResponsiveService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly daviplataService = inject(DaviplataService);
  private readonly matDialog = inject(MatDialog);
  private readonly datePipe = inject(DatePipe);
  private readonly decimalPipe = inject(DecimalPipe);

  step = signal(1);
  download = signal(false);
  detailsModel!: DetailsModel;
  transactionId = signal<string>('');
  verification = signal('');
  paymentDetail = signal<PaymentDetails | null>(null);
  isLoading = signal(true);
  form!: FormGroup;
  amount = computed(() => {
    const amount = this.decimalPipe.transform(this.paymentDetail()?.operation?.amount, '1.2-2');
    return `Pagar ${this.paymentDetail()?.operation?.currencySymbol} ${amount}`;
  });
  expirationDate = computed(() => {
    const expirationDate = this.paymentDetail()?.operation?.expirationDate;
    return expirationDate ? new Date(expirationDate): new Date();
  });

  ticketDetails: TicketDetails[] = [];
  backUrl = signal<string>('');

  ngOnInit() {

    this.detailsModel = {
      title: 'Pago Rechazado',
      img: IconsEnum.ERROR,
      color: 'red',
      mount: '$ 3,000.00'
    };

    this.transactionId.set(this.route.snapshot.paramMap.get('transactionId') ?? '');
    this.verification.set(this.route.snapshot.queryParamMap.get('verification') ?? '');
    this.getPaymentDetails();
  }

  get phoneControl() {
    return this.form.get('phone') as FormControl;
  }

  initForm() {
    this.form = new FormGroup({
      phone: new FormControl({
        value: '',
        disabled: true
      }, [Validators.required]),
      terms: new FormControl(false, [Validators.required, Validators.requiredTrue]),
    });

    const { customer: { phone }, attributes } = this.paymentDetail()!;
    if (phone) {
      const _phone = phone.replace(/^(\+57|57)/, '');
      this.phoneControl.setValue(_phone);
      const isPhoneEditable = attributes.find((attribute) => attribute.code === 'phone_editable')?.value === '1';
      if (isPhoneEditable) {
        this.phoneControl.enable();
      }
    } else {
      this.phoneControl.enable();
    }
  }

  getPaymentDetails() {
    const transactionId = this.transactionId();
    const verification = this.verification();

    if (!transactionId || !verification) {
      this.isLoading.set(false);
      this.router.navigate(['/error/transfiya']);
      return;
    }

    this.daviplataService.getPaymentDetails(transactionId, verification)
      .pipe(
        timeout(30_000),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: (response) => {
          queueMicrotask(() => {
            this.paymentDetail.set(response);
            this.initForm();
            const status = String(response.operation.status);
            if (status === Status.EXPIRED) {
              this.openErrorDialog();
            } else if (status === Status.DENIED || status === Status.APPROVED) {
              this.setTicketDetails(status, response.operation?.paymentDate, 'UTC');
            } else if (this.hasProcessorId(this.paymentDetail()?.attributes ?? [])) {
              this.step.set(2);
              this.getStatusTransfiya();
            }
          });
        },
        error: () => {
          this.router.navigate(['/error/transfiya']);
        },
      });
  }

  hasProcessorId(attributes: PaymentAttribute[]): boolean {
    return !!attributes.find((attribute) => attribute.code === 'processor_id')?.value;
  }

  downloadVoucher() {
    this.download.set(true);
    setTimeout(() => {
      this.screenRecordService.captureWithPromise('comprobante-pago', true).then((imageData: string) => {
        this.download.set(false);

        const link = document.createElement('a');
        link.href = imageData;
        link.download = 'transfiya-voucher.png';
        link.click();
      });
    }, 100);
  }

  openLoadingDialog() {
    return this.matDialog.open(CheckoutClinngModalLoadingComponent, {
      disableClose: true,
      width: "503px",
      height: "360px"
    });
  }

  openErrorDialog(title?: string, description?: string) {
    this.matDialog.open(UiClinngErrorModalComponent, {
      disableClose: true,
      width: "503px",
       height: '428px',
       data: {
        url: this.paymentDetail()?.operation?.errorUrl,
        title,
        description
       }
    });
  }

  onPay() {
    const dialog = this.openLoadingDialog();
    const id = this.paymentDetail()?.operation?.id;
    this.daviplataService.payTransfiya(id!, this.phoneControl.value, this.verification()).subscribe({
      next: () => {
        this.step.set(2);
        dialog.close();
        this.getStatusTransfiya();
      },
      error: (error) => { 
        this.isLoading.set(false);
        this.router.navigate(['/error/transfiya']);
        dialog.close();
      },
    });
  }

  getStatusTransfiya() {
    this.daviplataService.getStatusTransfiya(this.transactionId(), this.verification()).subscribe({
      next: (response) => {
        if (response.monnetOperationStatus === Status.PENDING) {
          setTimeout(() => {
            this.getStatusTransfiya();
          }, 60000)
        } else if (response.monnetOperationStatus === Status.APPROVED || response.monnetOperationStatus === Status.DENIED) {
          this.setTicketDetails(response.monnetOperationStatus, new Date());
        } else if (response.monnetOperationStatus === Status.EXPIRED) {
          this.openErrorDialog();
        } else {
          this.openErrorDialog('¡Ups! Error del servicio',  'Lo sentimos, hubo un error del servicio. Intenta continuar en breve.');
        }
      }
    });
  }

  setTicketDetails(status: string, date?: Date | string, format?: string ) {
    const amount = this.decimalPipe.transform(this.paymentDetail()?.operation?.amount, '1.2-2');
    this.ticketDetails = [
      { title: 'Número de línea', value: this.phoneControl.value },
      { title: 'Monto', value: `${this.paymentDetail()?.operation?.currencySymbol} ${amount}` },
      { title: 'Número de solicitud', value: this.paymentDetail()?.operation?.id?.toString() ?? '' },
    ];

    if (status === Status.APPROVED) {
      this.detailsModel = {
        title: 'Pago exitoso',
        img: IconsEnum.SUCCESS,
        color: 'blue',
        mount: `${this.paymentDetail()?.operation?.currencySymbol} ${amount}`
      };
      this.backUrl.set(this.paymentDetail()?.operation?.okUrl ?? '');
      this.ticketDetails.push({ title: 'Fecha y hora', value: this.datePipe.transform(date ?? new Date(), 'dd/MM/yyyy - h:mm a', format) ?? '' } );
    } else if (status === Status.DENIED) {
      this.detailsModel = {
        title: 'Pago rechazado',
        img: IconsEnum.ERROR,
        color: 'red',
        mount: `${this.paymentDetail()?.operation?.currencySymbol} ${amount}`
      };
      this.backUrl.set(this.paymentDetail()?.operation?.errorUrl ?? '');
    }
    this.step.set(3);
  }

  back() {
    window.location.href = this.backUrl();
  }
}
